import { z } from 'zod';
import {
  AttachmentMetadataSchema,
  ShiftbookEntryCreateRequestBaseSchema,
  ShiftbookEntryDetailSchema,
  ShiftbookEntryKeywordValueSchema,
  ShiftbookEntryPatchBaseSchema,
  ShiftbookResponsiblePartyReferenceSchema,
  SuggestionKapTagDescriptionSchema,
  SuggestionKapTagIdSchema,
  SuggestionKapTagNumberSchema,
  ShiftbookEntryWorkflowStatusSchema,
  ShiftbookKeywordIdSchema,
} from './shiftbook-entry.js';
import { LocaleSchema, type Locale, DEFAULT_LOCALE } from './locale.js';
import { SiteSchema, type Site } from './site.js';
import { AuthProviderSchema, GlobalRolesSchema, UserRoleSchema } from './user.js';

export * from './shiftbook-entry.js';
export * from './shiftbook-notifications.js';
export { LocaleSchema, DEFAULT_LOCALE, SiteSchema };
export type { Locale, Site };

// ---------------------------------------------------------------------------
// Lookup schemas (sites, areas, equipment, users)
// ---------------------------------------------------------------------------

export const SiteLookupSchema = z.object({
  id: SiteSchema,
  code: z.string(),
  name: z.string(),
  timeZone: z.string(),
});
export type SiteLookup = z.infer<typeof SiteLookupSchema>;

export const AreaLookupSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  site: SiteSchema,
});
export type AreaLookup = z.infer<typeof AreaLookupSchema>;

export const EquipmentLookupSchema = z.object({
  id: z.string(),
  kapNumber: z.string(),
  label: z.string(),
  areaId: z.string(),
  site: SiteSchema,
});
export type EquipmentLookup = z.infer<typeof EquipmentLookupSchema>;

export const UserLookupSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  role: z.string().optional(),
});
export type UserLookup = z.infer<typeof UserLookupSchema>;

export const LookupOptionSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string().min(1),
});
export type LookupOption = z.infer<typeof LookupOptionSchema>;

export const AreaRichOptionSchema = LookupOptionSchema.extend({
  code: z.string(),
  site: SiteSchema,
});
export type AreaRichOption = z.infer<typeof AreaRichOptionSchema>;

export const EquipmentRichOptionSchema = LookupOptionSchema.extend({
  kapNumber: z.string(),
  areaId: z.string(),
});
export type EquipmentRichOption = z.infer<typeof EquipmentRichOptionSchema>;

export const UserRichOptionSchema = LookupOptionSchema.extend({
  role: z.string().optional(),
});
export type UserRichOption = z.infer<typeof UserRichOptionSchema>;

export const GroupLookupOptionSchema = LookupOptionSchema.extend({
  memberCount: z.number().int().nonnegative(),
});
export type GroupLookupOption = z.infer<typeof GroupLookupOptionSchema>;

export const SiteOptionSchema = z.object({
  id: SiteSchema,
  displayName: z.string().min(1),
  timeZone: z.string().min(1),
});
export type SiteOption = z.infer<typeof SiteOptionSchema>;

export const SiteLookupResponseSchema = z.array(SiteOptionSchema);
export const AreaLookupResponseSchema = z.array(AreaRichOptionSchema);
export const EquipmentLookupResponseSchema = z.array(EquipmentRichOptionSchema);
export const UserLookupResponseSchema = z.array(UserRichOptionSchema);
export const GroupLookupResponseSchema = z.array(GroupLookupOptionSchema);
export const KeywordLookupOptionSchema = z.object({
  keywordId: ShiftbookKeywordIdSchema,
  value: ShiftbookEntryKeywordValueSchema,
  usageCount: z.number().int().nonnegative(),
});
export const KeywordLookupResponseSchema = z.array(KeywordLookupOptionSchema);
export const SuggestionKapTagLookupOptionSchema = z
  .object({
    tagId: SuggestionKapTagIdSchema,
    number: SuggestionKapTagNumberSchema,
    description: SuggestionKapTagDescriptionSchema,
  })
  .strict();
export const SuggestionKapTagLookupResponseSchema = z.array(SuggestionKapTagLookupOptionSchema);
export const ShiftbookAdminKeywordIdSchema = z.string().uuid();
export const ShiftbookAdminSuggestionKapTagIdSchema = SuggestionKapTagIdSchema;
export const ShiftbookTemplateIdSchema = z.string().uuid();
export const ShiftbookTemplateRevisionIdSchema = z.string().uuid();
export const ShiftbookTemplateLabelSchema = z.string().trim().min(1).max(255);
export const ShiftbookTemplateDescriptionSchema = z.string().max(4000);
export const ShiftbookKeywordScopeNodeTypeSchema = z.enum([
  'area',
  'line',
  'system',
  'equipment',
  'unknown',
]);
export type ShiftbookKeywordScopeNodeType = z.infer<typeof ShiftbookKeywordScopeNodeTypeSchema>;

export const ShiftbookKeywordScopeAssignmentSchema = z
  .object({
    flocId: z.string().uuid(),
    site: SiteSchema,
    code: z.string().min(1),
    name: z.string().min(1),
    nodeType: ShiftbookKeywordScopeNodeTypeSchema,
    locationPath: z.string().min(1),
    isShiftbookRelevant: z.boolean(),
  })
  .strict();
export type ShiftbookKeywordScopeAssignment = z.infer<typeof ShiftbookKeywordScopeAssignmentSchema>;

export const ShiftbookTemplateLookupOptionSchema = z
  .object({
    templateId: ShiftbookTemplateIdSchema,
    templateRevisionId: ShiftbookTemplateRevisionIdSchema,
    label: ShiftbookTemplateLabelSchema,
    defaultDescription: ShiftbookTemplateDescriptionSchema,
    versionNo: z.number().int().positive(),
  })
  .strict();
export const ShiftbookTemplateLookupResponseSchema = z.array(ShiftbookTemplateLookupOptionSchema);

export type SiteLookupResponse = z.infer<typeof SiteLookupResponseSchema>;
export type AreaLookupResponse = z.infer<typeof AreaLookupResponseSchema>;
export type EquipmentLookupResponse = z.infer<typeof EquipmentLookupResponseSchema>;
export type UserLookupResponse = z.infer<typeof UserLookupResponseSchema>;
export type GroupLookupResponse = z.infer<typeof GroupLookupResponseSchema>;
export type KeywordLookupOption = z.infer<typeof KeywordLookupOptionSchema>;
export type KeywordLookupResponse = z.infer<typeof KeywordLookupResponseSchema>;
export type SuggestionKapTagLookupOption = z.infer<typeof SuggestionKapTagLookupOptionSchema>;
export type SuggestionKapTagLookupResponse = z.infer<typeof SuggestionKapTagLookupResponseSchema>;
export type ShiftbookTemplateLookupOption = z.infer<typeof ShiftbookTemplateLookupOptionSchema>;
export type ShiftbookTemplateLookupResponse = z.infer<typeof ShiftbookTemplateLookupResponseSchema>;

// ---------------------------------------------------------------------------
// Error response (RFC 7807 Problem+JSON)
// ---------------------------------------------------------------------------

export const ProblemDetailSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string(),
  instance: z.string().optional(),
  traceId: z.string().optional(),
});
export type ProblemDetail = z.infer<typeof ProblemDetailSchema>;

export const ShiftbookKeywordLookupQuerySchema = z
  .object({
    site: SiteSchema.optional(),
    flocId: z.string().uuid('Invalid functional location ID').optional(),
    limit: z.coerce.number().int().positive().max(500).default(200),
    q: ShiftbookEntryKeywordValueSchema.optional(),
  })
  .strict();
export type ShiftbookKeywordLookupQuery = z.infer<typeof ShiftbookKeywordLookupQuerySchema>;

export const ShiftbookTemplateLookupQuerySchema = z
  .object({
    flocId: z.string().uuid('Invalid functional location ID'),
  })
  .strict();
export type ShiftbookTemplateLookupQuery = z.infer<typeof ShiftbookTemplateLookupQuerySchema>;

// ---------------------------------------------------------------------------
// Dev auth types
// ---------------------------------------------------------------------------

export const DevRoleSchema = z.enum(['viewer', 'editor', 'supervisor', 'admin', 'kiosk']);
export type DevRole = z.infer<typeof DevRoleSchema>;

export const SiteScopedRoleSchema = z.enum(['viewer', 'editor', 'supervisor', 'admin']);
export type SiteScopedRole = z.infer<typeof SiteScopedRoleSchema>;

export const SiteRolesSchema = z
  .object({
    aar: SiteScopedRoleSchema.optional(),
    fra: SiteScopedRoleSchema.optional(),
  })
  .strict()
  .refine((siteRoles) => Boolean(siteRoles.aar || siteRoles.fra), {
    message: 'At least one site role is required',
  });
export type SiteRoles = z.infer<typeof SiteRolesSchema>;

export const SiteRoleAssignmentsSchema = z
  .object({
    aar: SiteScopedRoleSchema.nullable(),
    fra: SiteScopedRoleSchema.nullable(),
  })
  .strict();
export type SiteRoleAssignments = z.infer<typeof SiteRoleAssignmentsSchema>;

export const AuthSessionSourceSchema = z.enum([
  'persona',
  'legacy-dev-role-cookie',
  'header',
  'm365',
  'local',
]);
export type AuthSessionSource = z.infer<typeof AuthSessionSourceSchema>;

export const AuthSessionProviderSchema = z.enum(['dev', 'm365', 'local']);
export type AuthSessionProvider = z.infer<typeof AuthSessionProviderSchema>;

export const AuthCapabilitySummarySchema = z
  .object({
    canReadEntries: z.boolean(),
    canCreateEntry: z.boolean(),
    canEditOwnEntry: z.boolean(),
    canEditAnyEntry: z.boolean(),
    canDeleteEntry: z.boolean(),
    canCompleteEntry: z.boolean(),
    canReopenEntry: z.boolean(),
    canManageUsers: z.boolean(),
  })
  .strict();
export type AuthCapabilitySummary = z.infer<typeof AuthCapabilitySummarySchema>;

export const AUTH_PERSONA_PRESET_IDS = [
  'aar-viewer',
  'aar-editor',
  'aar-supervisor',
  'aar-admin',
  'fra-viewer',
  'fra-editor',
  'fra-supervisor',
  'fra-admin',
  'cross-site-supervisor',
] as const;
export const AuthPersonaPresetIdSchema = z.enum(AUTH_PERSONA_PRESET_IDS);
export type AuthPersonaPresetId = z.infer<typeof AuthPersonaPresetIdSchema>;

export const AuthPersonaSessionIdSchema = z.union([AuthPersonaPresetIdSchema, z.literal('custom')]);
export type AuthPersonaSessionId = z.infer<typeof AuthPersonaSessionIdSchema>;

export const AuthPersonaPresetSchema = z
  .object({
    id: AuthPersonaPresetIdSchema,
    label: z.string().min(1),
    description: z.string().min(1),
    siteRoles: SiteRolesSchema,
    activeSite: SiteSchema,
  })
  .strict()
  .refine((preset) => Boolean(preset.siteRoles[preset.activeSite]), {
    message: 'activeSite must match one of the configured site roles',
    path: ['activeSite'],
  });
export type AuthPersonaPreset = z.infer<typeof AuthPersonaPresetSchema>;

export const AUTH_PERSONA_PRESETS: readonly AuthPersonaPreset[] = [
  {
    id: 'aar-viewer',
    label: 'AAR Viewer',
    description: 'Read-only access for Aarberg.',
    siteRoles: { aar: 'viewer' },
    activeSite: 'aar',
  },
  {
    id: 'aar-editor',
    label: 'AAR Editor',
    description: 'Create and edit own entries in Aarberg.',
    siteRoles: { aar: 'editor' },
    activeSite: 'aar',
  },
  {
    id: 'aar-supervisor',
    label: 'AAR Supervisor',
    description: 'Supervisor override permissions in Aarberg.',
    siteRoles: { aar: 'supervisor' },
    activeSite: 'aar',
  },
  {
    id: 'aar-admin',
    label: 'AAR Admin',
    description: 'Administrative permissions in Aarberg.',
    siteRoles: { aar: 'admin' },
    activeSite: 'aar',
  },
  {
    id: 'fra-viewer',
    label: 'FRA Viewer',
    description: 'Read-only access for Frauenfeld.',
    siteRoles: { fra: 'viewer' },
    activeSite: 'fra',
  },
  {
    id: 'fra-editor',
    label: 'FRA Editor',
    description: 'Create and edit own entries in Frauenfeld.',
    siteRoles: { fra: 'editor' },
    activeSite: 'fra',
  },
  {
    id: 'fra-supervisor',
    label: 'FRA Supervisor',
    description: 'Supervisor override permissions in Frauenfeld.',
    siteRoles: { fra: 'supervisor' },
    activeSite: 'fra',
  },
  {
    id: 'fra-admin',
    label: 'FRA Admin',
    description: 'Administrative permissions in Frauenfeld.',
    siteRoles: { fra: 'admin' },
    activeSite: 'fra',
  },
  {
    id: 'cross-site-supervisor',
    label: 'Cross-Site Supervisor',
    description: 'Supervisor permissions for Aarberg and Frauenfeld.',
    siteRoles: { aar: 'supervisor', fra: 'supervisor' },
    activeSite: 'aar',
  },
] as const;

export const AuthPersonaCatalogResponseSchema = z.array(AuthPersonaPresetSchema);
export type AuthPersonaCatalogResponse = z.infer<typeof AuthPersonaCatalogResponseSchema>;

export const AuthModeSchema = z.enum(['dev', 'm365']);
export type AuthMode = z.infer<typeof AuthModeSchema>;

export const AuthSessionUserSchema = z
  .object({
    id: z.string(),
    username: z.string(),
    displayName: z.string(),
    provider: AuthSessionProviderSchema.optional(),
    locale: LocaleSchema,
    siteRoles: SiteRolesSchema,
    activeSite: SiteSchema,
    activeRole: SiteScopedRoleSchema,
    availableSites: z.array(SiteSchema).min(1),
    personaId: AuthPersonaSessionIdSchema,
    capabilities: AuthCapabilitySummarySchema,
    sessionSource: AuthSessionSourceSchema,
    globalRoles: GlobalRolesSchema.optional(),
  })
  .strict();
export type AuthSessionUser = z.infer<typeof AuthSessionUserSchema>;

export const AuthUnauthenticatedResponseSchema = z
  .object({
    authenticated: z.literal(false),
    authMode: AuthModeSchema,
  })
  .strict();
export type AuthUnauthenticatedResponse = z.infer<typeof AuthUnauthenticatedResponseSchema>;

export const AuthAuthenticatedResponseSchema = z
  .object({
    authenticated: z.literal(true),
    authMode: AuthModeSchema,
    user: AuthSessionUserSchema,
  })
  .strict();
export type AuthAuthenticatedResponse = z.infer<typeof AuthAuthenticatedResponseSchema>;

export const AuthMeResponseSchema = z.discriminatedUnion('authenticated', [
  AuthUnauthenticatedResponseSchema,
  AuthAuthenticatedResponseSchema,
]);
export type AuthMeResponse = z.infer<typeof AuthMeResponseSchema>;

export const AuthLoginPresetRequestSchema = z
  .object({
    type: z.literal('preset'),
    presetId: AuthPersonaPresetIdSchema,
  })
  .strict();
export type AuthLoginPresetRequest = z.infer<typeof AuthLoginPresetRequestSchema>;

export const AuthLoginCustomRequestSchema = z
  .object({
    type: z.literal('custom'),
    siteRoles: SiteRolesSchema,
    activeSite: SiteSchema,
  })
  .strict()
  .refine((payload) => Boolean(payload.siteRoles[payload.activeSite]), {
    message: 'activeSite must match one of the configured site roles',
    path: ['activeSite'],
  });
export type AuthLoginCustomRequest = z.infer<typeof AuthLoginCustomRequestSchema>;

export const AuthLoginRequestSchema = z.discriminatedUnion('type', [
  AuthLoginPresetRequestSchema,
  AuthLoginCustomRequestSchema,
]);
export type AuthLoginRequest = z.infer<typeof AuthLoginRequestSchema>;

export const AuthSetActiveSiteRequestSchema = z
  .object({
    activeSite: SiteSchema,
  })
  .strict();
export type AuthSetActiveSiteRequest = z.infer<typeof AuthSetActiveSiteRequestSchema>;

// ---------------------------------------------------------------------------
// Admin user-management contracts
// ---------------------------------------------------------------------------

export const ShiftbookAdminUserSchema = z
  .object({
    id: z.string().uuid(),
    username: z.string().min(1),
    displayName: z.string().min(1),
    email: z.string().email().nullable(),
    authProvider: AuthProviderSchema,
    active: z.boolean(),
    canLogin: z.boolean(),
    maxRole: UserRoleSchema,
    globalRoles: GlobalRolesSchema.optional(),
    siteRoles: SiteRoleAssignmentsSchema,
  })
  .strict();
export type ShiftbookAdminUser = z.infer<typeof ShiftbookAdminUserSchema>;

export const ShiftbookAdminUsersResponseSchema = z.array(ShiftbookAdminUserSchema);
export type ShiftbookAdminUsersResponse = z.infer<typeof ShiftbookAdminUsersResponseSchema>;

export const ShiftbookAdminGroupSchema = z
  .object({
    id: z.string().uuid(),
    site: SiteSchema,
    displayName: z.string().min(1),
    description: z.string().nullable(),
    isActive: z.boolean(),
    memberCount: z.number().int().nonnegative(),
    memberIds: z.array(z.string().uuid()),
  })
  .strict();
export type ShiftbookAdminGroup = z.infer<typeof ShiftbookAdminGroupSchema>;

export const ShiftbookAdminGroupsResponseSchema = z.array(ShiftbookAdminGroupSchema);
export type ShiftbookAdminGroupsResponse = z.infer<typeof ShiftbookAdminGroupsResponseSchema>;

export const ShiftbookAdminGroupCreateSchema = z
  .object({
    site: SiteSchema,
    name: z.string().trim().min(1).max(128),
    description: z.string().trim().max(400).nullable().optional(),
  })
  .strict();
export type ShiftbookAdminGroupCreate = z.infer<typeof ShiftbookAdminGroupCreateSchema>;

export const ShiftbookAdminGroupPatchSchema = z
  .object({
    name: z.string().trim().min(1).max(128).optional(),
    description: z.string().trim().max(400).nullable().optional(),
    isActive: z.boolean().optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'At least one field is required');
export type ShiftbookAdminGroupPatch = z.infer<typeof ShiftbookAdminGroupPatchSchema>;

export const ShiftbookAdminGroupMembersSchema = z
  .object({
    memberIds: z.array(z.string().uuid()),
    // The member set the client last observed. The server computes removals
    // against this baseline so memberships that were invisible to the client
    // (e.g. users inactive at fetch time) are never deleted by a stale save.
    baselineMemberIds: z.array(z.string().uuid()),
  })
  .strict();
export type ShiftbookAdminGroupMembers = z.infer<typeof ShiftbookAdminGroupMembersSchema>;

export const ShiftbookAdminUserSiteRolesPatchSchema = z
  .object({
    siteRoles: SiteRoleAssignmentsSchema,
  })
  .strict();
export type ShiftbookAdminUserSiteRolesPatch = z.infer<
  typeof ShiftbookAdminUserSiteRolesPatchSchema
>;

export const ShiftbookAdminUserSiteRolesResponseSchema = ShiftbookAdminUserSchema;
export type ShiftbookAdminUserSiteRolesResponse = z.infer<
  typeof ShiftbookAdminUserSiteRolesResponseSchema
>;

// ---------------------------------------------------------------------------
// Admin functional-location contracts
// ---------------------------------------------------------------------------

export const ShiftbookFlocNodeTypeSchema = z.enum([
  'site',
  'area',
  'line',
  'system',
  'equipment',
  'unknown',
]);
export type ShiftbookFlocNodeType = z.infer<typeof ShiftbookFlocNodeTypeSchema>;

export const FlocTreeNodeIdSchema = z.union([SiteSchema, z.string().uuid()]);
export type FlocTreeNodeId = z.infer<typeof FlocTreeNodeIdSchema>;

export const ShiftbookAdminFlocNodeSchema = z
  .object({
    id: FlocTreeNodeIdSchema,
    site: SiteSchema,
    parentId: FlocTreeNodeIdSchema.nullable(),
    code: z.string().min(1),
    name: z.string().min(1),
    nodeType: ShiftbookFlocNodeTypeSchema,
    locationPath: z.string().min(1),
    locationDepth: z.number().int().nonnegative(),
    sortIndex: z.number().int(),
    isActive: z.boolean(),
    isSystem: z.boolean(),
    isShiftbookRelevant: z.boolean(),
  })
  .strict();
export type ShiftbookAdminFlocNode = z.infer<typeof ShiftbookAdminFlocNodeSchema>;

export const ShiftbookAdminFlocListResponseSchema = z
  .object({
    items: z.array(ShiftbookAdminFlocNodeSchema),
  })
  .strict();
export type ShiftbookAdminFlocListResponse = z.infer<typeof ShiftbookAdminFlocListResponseSchema>;

export const ShiftbookAdminFlocPatchSchema = z
  .object({
    name: z.string().trim().min(1).max(255).optional(),
    parentId: FlocTreeNodeIdSchema.optional(),
    isShiftbookRelevant: z.boolean().optional(),
  })
  .strict()
  .refine(
    (data) =>
      data.name !== undefined ||
      data.parentId !== undefined ||
      data.isShiftbookRelevant !== undefined,
    { message: 'At least one of name, parentId, or isShiftbookRelevant must be provided' },
  );
export type ShiftbookAdminFlocPatch = z.infer<typeof ShiftbookAdminFlocPatchSchema>;

export const ShiftbookAdminFlocDeactivateRequestSchema = z
  .object({
    confirmCascade: z.literal(true),
  })
  .strict();
export type ShiftbookAdminFlocDeactivateRequest = z.infer<
  typeof ShiftbookAdminFlocDeactivateRequestSchema
>;

export const ShiftbookAdminFlocMutationSummarySchema = z
  .object({
    nodeId: FlocTreeNodeIdSchema,
    site: SiteSchema,
    affectedNodeCount: z.number().int().nonnegative(),
    changedNodeCount: z.number().int().nonnegative(),
  })
  .strict();
export type ShiftbookAdminFlocMutationSummary = z.infer<
  typeof ShiftbookAdminFlocMutationSummarySchema
>;

// ---------------------------------------------------------------------------
// Admin keyword catalog contracts
// ---------------------------------------------------------------------------

export const ShiftbookAdminKeywordSchema = z
  .object({
    keywordId: ShiftbookAdminKeywordIdSchema,
    value: ShiftbookEntryKeywordValueSchema,
    scopes: z.array(ShiftbookKeywordScopeAssignmentSchema).min(1),
    isActive: z.boolean(),
    usageCount: z.number().int().nonnegative(),
  })
  .strict();
export type ShiftbookAdminKeyword = z.infer<typeof ShiftbookAdminKeywordSchema>;

export const ShiftbookAdminKeywordListResponseSchema = z
  .object({
    items: z.array(ShiftbookAdminKeywordSchema),
  })
  .strict();
export type ShiftbookAdminKeywordListResponse = z.infer<
  typeof ShiftbookAdminKeywordListResponseSchema
>;

export const ShiftbookAdminKeywordCreateSchema = z
  .object({
    value: ShiftbookEntryKeywordValueSchema,
    scopeFlocIds: z.array(z.string().uuid('Invalid functional location ID')).min(1),
  })
  .strict();
export type ShiftbookAdminKeywordCreate = z.infer<typeof ShiftbookAdminKeywordCreateSchema>;

export const ShiftbookAdminKeywordPatchSchema = z
  .object({
    value: ShiftbookEntryKeywordValueSchema.optional(),
    scopeFlocIds: z.array(z.string().uuid('Invalid functional location ID')).min(1).optional(),
  })
  .strict()
  .refine((data) => data.value !== undefined || data.scopeFlocIds !== undefined, {
    message: 'At least one of value or scopeFlocIds must be provided',
  });
export type ShiftbookAdminKeywordPatch = z.infer<typeof ShiftbookAdminKeywordPatchSchema>;

export const ShiftbookAdminKeywordResponseSchema = ShiftbookAdminKeywordSchema;
export type ShiftbookAdminKeywordResponse = z.infer<typeof ShiftbookAdminKeywordResponseSchema>;

// ---------------------------------------------------------------------------
// Admin suggestion KAP tag contracts
// ---------------------------------------------------------------------------

export const ShiftbookAdminSuggestionKapTagSchema = z
  .object({
    tagId: ShiftbookAdminSuggestionKapTagIdSchema,
    site: SiteSchema,
    number: SuggestionKapTagNumberSchema,
    description: SuggestionKapTagDescriptionSchema,
    isActive: z.boolean(),
    usageCount: z.number().int().nonnegative(),
  })
  .strict();
export type ShiftbookAdminSuggestionKapTag = z.infer<typeof ShiftbookAdminSuggestionKapTagSchema>;

export const ShiftbookAdminSuggestionKapTagListResponseSchema = z
  .object({
    items: z.array(ShiftbookAdminSuggestionKapTagSchema),
  })
  .strict();
export type ShiftbookAdminSuggestionKapTagListResponse = z.infer<
  typeof ShiftbookAdminSuggestionKapTagListResponseSchema
>;

export const ShiftbookAdminSuggestionKapTagCreateSchema = z
  .object({
    site: SiteSchema,
    number: SuggestionKapTagNumberSchema,
    description: SuggestionKapTagDescriptionSchema,
  })
  .strict();
export type ShiftbookAdminSuggestionKapTagCreate = z.infer<
  typeof ShiftbookAdminSuggestionKapTagCreateSchema
>;

export const ShiftbookAdminSuggestionKapTagPatchSchema = z
  .object({
    number: SuggestionKapTagNumberSchema.optional(),
    description: SuggestionKapTagDescriptionSchema.optional(),
  })
  .strict()
  .refine((data) => data.number !== undefined || data.description !== undefined, {
    message: 'At least one of number or description must be provided',
  });
export type ShiftbookAdminSuggestionKapTagPatch = z.infer<
  typeof ShiftbookAdminSuggestionKapTagPatchSchema
>;

export const ShiftbookAdminSuggestionKapTagResponseSchema = ShiftbookAdminSuggestionKapTagSchema;
export type ShiftbookAdminSuggestionKapTagResponse = z.infer<
  typeof ShiftbookAdminSuggestionKapTagResponseSchema
>;

// ---------------------------------------------------------------------------
// Admin template contracts
// ---------------------------------------------------------------------------

export const ShiftbookTemplateScopeNodeTypeSchema = z.enum(['area', 'line', 'system', 'equipment']);
export type ShiftbookTemplateScopeNodeType = z.infer<typeof ShiftbookTemplateScopeNodeTypeSchema>;

export const ShiftbookTemplateScopeAssignmentSchema = z
  .object({
    flocId: z.string().uuid(),
    site: SiteSchema,
    code: z.string().min(1),
    name: z.string().min(1),
    nodeType: ShiftbookTemplateScopeNodeTypeSchema,
    locationPath: z.string().min(1),
    isShiftbookRelevant: z.boolean(),
  })
  .strict();
export type ShiftbookTemplateScopeAssignment = z.infer<
  typeof ShiftbookTemplateScopeAssignmentSchema
>;

export const ShiftbookAdminTemplateRevisionSchema = z
  .object({
    revisionId: ShiftbookTemplateRevisionIdSchema,
    versionNo: z.number().int().positive(),
    label: ShiftbookTemplateLabelSchema,
    defaultDescription: ShiftbookTemplateDescriptionSchema,
    isCurrent: z.boolean(),
    isSelectable: z.boolean(),
  })
  .strict();
export type ShiftbookAdminTemplateRevision = z.infer<typeof ShiftbookAdminTemplateRevisionSchema>;

export const ShiftbookAdminTemplateSchema = z
  .object({
    templateId: ShiftbookTemplateIdSchema,
    currentRevisionId: ShiftbookTemplateRevisionIdSchema,
    label: ShiftbookTemplateLabelSchema,
    defaultDescription: ShiftbookTemplateDescriptionSchema,
    isActive: z.boolean(),
    usageCount: z.number().int().nonnegative(),
    currentVersionNo: z.number().int().positive(),
    scopes: z.array(ShiftbookTemplateScopeAssignmentSchema).min(1),
    revisions: z.array(ShiftbookAdminTemplateRevisionSchema).min(1),
  })
  .strict();
export type ShiftbookAdminTemplate = z.infer<typeof ShiftbookAdminTemplateSchema>;

export const ShiftbookAdminTemplateListResponseSchema = z
  .object({
    items: z.array(ShiftbookAdminTemplateSchema),
  })
  .strict();
export type ShiftbookAdminTemplateListResponse = z.infer<
  typeof ShiftbookAdminTemplateListResponseSchema
>;

export const ShiftbookAdminTemplateCreateSchema = z
  .object({
    label: ShiftbookTemplateLabelSchema,
    defaultDescription: ShiftbookTemplateDescriptionSchema,
    scopeFlocIds: z.array(z.string().uuid('Invalid functional location ID')).min(1),
  })
  .strict();
export type ShiftbookAdminTemplateCreate = z.infer<typeof ShiftbookAdminTemplateCreateSchema>;

export const ShiftbookAdminTemplatePatchSchema = z
  .object({
    label: ShiftbookTemplateLabelSchema.optional(),
    defaultDescription: ShiftbookTemplateDescriptionSchema.optional(),
    scopeFlocIds: z.array(z.string().uuid('Invalid functional location ID')).min(1).optional(),
  })
  .strict()
  .refine(
    (data) =>
      data.label !== undefined ||
      data.defaultDescription !== undefined ||
      data.scopeFlocIds !== undefined,
    {
      message: 'At least one of label, defaultDescription, or scopeFlocIds must be provided',
    },
  );
export type ShiftbookAdminTemplatePatch = z.infer<typeof ShiftbookAdminTemplatePatchSchema>;

export const LegacyDevAuthUserSchema = z.object({
  id: z.string(),
  username: z.string(),
  displayName: z.string(),
  role: DevRoleSchema,
  locale: LocaleSchema,
});
export type LegacyDevAuthUser = z.infer<typeof LegacyDevAuthUserSchema>;
export const DevAuthMeResponseSchema = LegacyDevAuthUserSchema;
export type DevAuthMeResponse = LegacyDevAuthUser;

export const SwitchRoleRequestSchema = z.object({
  role: DevRoleSchema,
});
export type SwitchRoleRequest = z.infer<typeof SwitchRoleRequestSchema>;

// ---------------------------------------------------------------------------
// Entry workspace save (batch save for staged dialog changes)
// ---------------------------------------------------------------------------

export const ShiftbookEntryWorkspaceSaveModeSchema = z.enum(['create', 'update']);
export type ShiftbookEntryWorkspaceSaveMode = z.infer<typeof ShiftbookEntryWorkspaceSaveModeSchema>;

export const ShiftbookEntryWorkspaceCreateEntrySchema =
  ShiftbookEntryCreateRequestBaseSchema.extend({
    responsible: ShiftbookResponsiblePartyReferenceSchema.nullable().optional(),
    keywordIds: z.array(ShiftbookKeywordIdSchema).default([]),
  })
    .strict()
    .superRefine((value, ctx) => {
      if (value.type !== 'suggestion' && value.suggestionKapTagId !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['suggestionKapTagId'],
          message: 'Suggestion KAP tag IDs are only allowed for suggestion entries',
        });
      }
    });
export type ShiftbookEntryWorkspaceCreateEntry = z.infer<
  typeof ShiftbookEntryWorkspaceCreateEntrySchema
>;

export const ShiftbookEntryWorkspaceUpdatePatchSchema = ShiftbookEntryPatchBaseSchema.extend({
  status: ShiftbookEntryWorkflowStatusSchema.optional(),
  keywordIds: z.array(ShiftbookKeywordIdSchema).optional(),
})
  .strict()
  .superRefine((value, ctx) => {
    if (value.suggestionKapTagId && value.type !== 'suggestion') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['suggestionKapTagId'],
        message: 'Non-null suggestion KAP tag IDs require the patch type to be set to suggestion',
      });
    }
  });
export type ShiftbookEntryWorkspaceUpdatePatch = z.infer<
  typeof ShiftbookEntryWorkspaceUpdatePatchSchema
>;

export const ShiftbookEntryWorkspaceEntryPayloadSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('create'),
    entry: ShiftbookEntryWorkspaceCreateEntrySchema,
  }),
  z.object({
    mode: z.literal('update'),
    entryId: z.string().uuid(),
    version: z.number().int().positive(),
    patch: ShiftbookEntryWorkspaceUpdatePatchSchema,
  }),
]);
export type ShiftbookEntryWorkspaceEntryPayload = z.infer<
  typeof ShiftbookEntryWorkspaceEntryPayloadSchema
>;

export const ShiftbookEntryWorkspaceCommentDraftSchema = z.object({
  clientId: z.string().uuid(),
  body: z.string().min(1),
  parentCommentId: z.string().uuid().nullable().optional(),
});
export type ShiftbookEntryWorkspaceCommentDraft = z.infer<
  typeof ShiftbookEntryWorkspaceCommentDraftSchema
>;

export const ShiftbookEntryWorkspaceAttachmentDraftSchema = z.object({
  clientId: z.string().uuid(),
  filename: z.string().min(1).optional(),
  mimeType: z.string().min(1).optional(),
});
export type ShiftbookEntryWorkspaceAttachmentDraft = z.infer<
  typeof ShiftbookEntryWorkspaceAttachmentDraftSchema
>;

// ---------------------------------------------------------------------------
// Checklist staged operation schemas (workspace batch save)
// ---------------------------------------------------------------------------

export const ChecklistStagedAddSchema = z.object({
  clientId: z.string().uuid(),
  text: z
    .string()
    .min(1, 'Checklist item text is required')
    .max(2000, 'Checklist item text must not exceed 2000 characters'),
  isDone: z.boolean().default(false),
  position: z.number().int().nonnegative().optional(),
});
export type ChecklistStagedAdd = z.infer<typeof ChecklistStagedAddSchema>;

export const ChecklistStagedUpdateSchema = z
  .object({
    id: z.string().uuid(),
    text: z
      .string()
      .min(1, 'Checklist item text is required')
      .max(2000, 'Checklist item text must not exceed 2000 characters')
      .optional(),
    isDone: z.boolean().optional(),
  })
  .refine((data) => data.text !== undefined || data.isDone !== undefined, {
    message: 'At least one of text or isDone must be provided',
  });
export type ChecklistStagedUpdate = z.infer<typeof ChecklistStagedUpdateSchema>;

export const ChecklistReorderRefSchema = z.discriminatedUnion('ref', [
  z.object({ ref: z.literal('id'), id: z.string().uuid() }),
  z.object({ ref: z.literal('clientId'), clientId: z.string().uuid() }),
]);
export type ChecklistReorderRef = z.infer<typeof ChecklistReorderRefSchema>;

export const ShiftbookEntryWorkspaceChecklistOpsSchema = z.object({
  add: z.array(ChecklistStagedAddSchema).default([]),
  update: z.array(ChecklistStagedUpdateSchema).default([]),
  delete: z.array(z.string().uuid()).default([]),
  reorder: z.array(ChecklistReorderRefSchema).default([]),
});
export type ShiftbookEntryWorkspaceChecklistOps = z.infer<
  typeof ShiftbookEntryWorkspaceChecklistOpsSchema
>;

export const ShiftbookEntryWorkspaceSaveRequestSchema = z.object({
  entry: ShiftbookEntryWorkspaceEntryPayloadSchema,
  comments: z.array(ShiftbookEntryWorkspaceCommentDraftSchema).default([]),
  attachments: z
    .object({
      add: z.array(ShiftbookEntryWorkspaceAttachmentDraftSchema).default([]),
      delete: z.array(z.string().uuid()).default([]),
    })
    .default({ add: [], delete: [] }),
  checklist: ShiftbookEntryWorkspaceChecklistOpsSchema.default({
    add: [],
    update: [],
    delete: [],
    reorder: [],
  }),
});
export type ShiftbookEntryWorkspaceSaveRequest = z.infer<
  typeof ShiftbookEntryWorkspaceSaveRequestSchema
>;

export const ShiftbookEntryWorkspaceCommentResultSchema = z.object({
  clientId: z.string().uuid(),
  id: z.string().uuid(),
});
export type ShiftbookEntryWorkspaceCommentResult = z.infer<
  typeof ShiftbookEntryWorkspaceCommentResultSchema
>;

export const ShiftbookEntryWorkspaceAttachmentUploadResultSchema = z.object({
  clientId: z.string().uuid(),
  status: z.enum(['uploaded', 'failed', 'skipped_forbidden']),
  attachment: AttachmentMetadataSchema.optional(),
  error: z.string().optional(),
});
export type ShiftbookEntryWorkspaceAttachmentUploadResult = z.infer<
  typeof ShiftbookEntryWorkspaceAttachmentUploadResultSchema
>;

export const ShiftbookEntryWorkspaceAttachmentDeleteResultSchema = z.object({
  attachmentId: z.string().uuid(),
  status: z.enum(['deleted', 'not_found', 'forbidden', 'failed', 'skipped_forbidden']),
  error: z.string().optional(),
});
export type ShiftbookEntryWorkspaceAttachmentDeleteResult = z.infer<
  typeof ShiftbookEntryWorkspaceAttachmentDeleteResultSchema
>;

export const ShiftbookEntryWorkspaceChecklistResultSchema = z.object({
  clientId: z.string().uuid(),
  id: z.string().uuid(),
});
export type ShiftbookEntryWorkspaceChecklistResult = z.infer<
  typeof ShiftbookEntryWorkspaceChecklistResultSchema
>;

export const ShiftbookEntryWorkspaceSaveResponseSchema = z.object({
  entryId: z.string().uuid(),
  entryDetail: ShiftbookEntryDetailSchema,
  comments: z.array(ShiftbookEntryWorkspaceCommentResultSchema).default([]),
  attachmentUploads: z.array(ShiftbookEntryWorkspaceAttachmentUploadResultSchema).default([]),
  attachmentDeletes: z.array(ShiftbookEntryWorkspaceAttachmentDeleteResultSchema).default([]),
  checklist: z.array(ShiftbookEntryWorkspaceChecklistResultSchema).default([]),
});
export type ShiftbookEntryWorkspaceSaveResponse = z.infer<
  typeof ShiftbookEntryWorkspaceSaveResponseSchema
>;
